import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
} from '@/components/DataTable';
import {
  listProjectsDataTable,
  getProjectBySlug,
  getProjectCoverage,
  getProjectFilterOptions,
  rescanProjectCoverage,
} from '@kirin/api-client/api/projects';
import type {
  ProjectDataTableItem,
  ProjectDataTableSortField,
  ProjectDetail,
  ProjectCoverage,
  ProjectFilterOptionField,
} from '@kirin/api-client/contracts/projects';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/hooks/api/queryKeys';

/**
 * Fetch projects for the Inventory → Projects DataTable (KIR-3356).
 */
export async function fetchProjectsDataTable(
  organizationId: string,
  params: DataFetchParams,
  signal?: AbortSignal
): Promise<DataFetchResponse<ProjectDataTableItem>> {
  const response = await listProjectsDataTable(
    apiClient,
    organizationId,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: params.sort?.field as ProjectDataTableSortField | undefined,
      sortOrder: params.sort?.order,
      search: params.search,
      knownTotal: params.knownTotal,
    },
    { signal }
  );

  return {
    data: response.data,
    pagination: response.pagination,
    meta: {
      activeFilters: response.meta?.activeFilters ?? [],
      search: response.meta?.search,
    },
  };
}

/**
 * Fetch a single project by slug for the single-project page (KIR-3356).
 */
export async function fetchProjectBySlug(
  organizationId: string,
  slug: string,
  signal?: AbortSignal
): Promise<ProjectDetail> {
  return getProjectBySlug(apiClient, organizationId, slug, { signal });
}

/**
 * Fetch a project's security-coverage payload for the single-project page
 * (KIR-3517): project metadata, last scan time, summary counts, and every
 * catalog category with its per-project status.
 */
export async function fetchProjectCoverage(
  organizationId: string,
  slug: string,
  signal?: AbortSignal
): Promise<ProjectCoverage> {
  return getProjectCoverage(apiClient, organizationId, slug, { signal });
}

/**
 * Queue a fresh coverage scan (KIR-3516). Resolves once the scan is enqueued —
 * the page re-fetches coverage to pick up the new results when they land.
 */
export async function requestProjectCoverageRescan(
  organizationId: string,
  slug: string
): Promise<void> {
  await rescanProjectCoverage(apiClient, organizationId, slug);
}

/**
 * Server-backed distinct-value options for the name / repo-URL column filters
 * (KIR-3356) — mirrors the Extensions name filter.
 */
export async function fetchProjectFilterOptions(
  organizationId: string,
  field: ProjectFilterOptionField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getProjectFilterOptions(apiClient, organizationId, {
    field,
    search: params.search,
    limit: params.limit ?? 10,
    offset: params.offset ?? 0,
  });
  return {
    options: response.options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}

/** Query key for the name / repo-URL filter-option dropdowns. */
export function getProjectFilterOptionsQueryKey(
  organizationId: string,
  field: ProjectFilterOptionField,
  params: { search?: string; limit?: number; offset?: number }
): readonly unknown[] {
  return queryKeys.projects.filterOptions(organizationId, field, params);
}
